import { useEffect } from 'react';

export default function PageSEO({ title, description, canonical, noIndex = false, isPrivate = false, schema = null, ogType = 'website', image }) {
  useEffect(() => {
    // 1. Title
    const finalTitle = title || 'AI Search Advisers';
    document.title = finalTitle;

    // Helper to update or create meta tags
    const setMetaTag = (selector, nameOrProperty, value, attribute = 'name') => {
      const existing = [...document.querySelectorAll(selector)];
      existing.slice(1).forEach(tag => tag.remove());
      let meta = existing[0];
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, nameOrProperty);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', value);
    };

    // Helper to remove meta tags
    const removeMetaTag = (selector) => {
      const meta = document.querySelector(selector);
      if (meta) {
        meta.remove();
      }
    };

    // 2. Robots
    if (noIndex || isPrivate) {
      setMetaTag('meta[name="robots"]', 'robots', 'noindex, nofollow');
    } else {
      setMetaTag('meta[name="robots"]', 'robots', 'index, follow');
    }

    // 3. Description
    if (description && !isPrivate) {
      setMetaTag('meta[name="description"]', 'description', description);
    } else {
      removeMetaTag('meta[name="description"]');
    }

    // 4. Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonical && !isPrivate && !noIndex) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    } else if (canonicalLink) {
      canonicalLink.remove();
    }

    // 5. Open Graph
    if (!isPrivate && !noIndex) {
      setMetaTag('meta[property="og:title"]', 'og:title', finalTitle, 'property');
      setMetaTag('meta[property="og:type"]', 'og:type', ogType, 'property');
      if (description) {
        setMetaTag('meta[property="og:description"]', 'og:description', description, 'property');
      } else {
        removeMetaTag('meta[property="og:description"]');
      }
      if (canonical) {
        setMetaTag('meta[property="og:url"]', 'og:url', canonical, 'property');
      } else {
        removeMetaTag('meta[property="og:url"]');
      }
    } else {
      removeMetaTag('meta[property="og:title"]');
      removeMetaTag('meta[property="og:type"]');
      removeMetaTag('meta[property="og:description"]');
      removeMetaTag('meta[property="og:url"]');
    }

    // 6. Twitter
    if (!isPrivate && !noIndex) {
      setMetaTag('meta[name="twitter:card"]', 'twitter:card', 'summary_large_image');
      setMetaTag('meta[name="twitter:title"]', 'twitter:title', finalTitle);
      if (description) {
        setMetaTag('meta[name="twitter:description"]', 'twitter:description', description);
      } else {
        removeMetaTag('meta[name="twitter:description"]');
      }
    } else {
      removeMetaTag('meta[name="twitter:card"]');
      removeMetaTag('meta[name="twitter:title"]');
      removeMetaTag('meta[name="twitter:description"]');
    }

    for (const [attribute, key] of [['property', 'og:image'], ['name', 'twitter:image']]) {
      const selector = `meta[${attribute}="${key}"]`;
      if (image && !isPrivate && !noIndex) setMetaTag(selector, key, image, attribute);
      else document.querySelectorAll(selector).forEach(tag => tag.remove());
    }
    // 7. JSON-LD Schema
    let schemaScript = document.querySelector('script[id="page-structured-data"]');
    if (schema && !isPrivate && !noIndex) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'page-structured-data';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }

    // Cleanup on unmount for schema specifically
    return () => {
      const script = document.querySelector('script[id="page-structured-data"]');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, canonical, noIndex, isPrivate, schema, ogType, image]);

  return null;
}
